import { useState, useEffect } from "react"
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Chip,
  Grid,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  RadioGroup,
  Radio,
  FormLabel,
  IconButton,
  Paper,
} from "@mui/material"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { Groups } from "@mui/icons-material" // Icono para el botón de empleados
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import FullCalendar from "@fullcalendar/react"
import esLocale from "@fullcalendar/core/locales/es"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin from "@fullcalendar/interaction"
import fetchwrapper from "../../../services/interceptors/fetchwrapper"
import Swal from "sweetalert2"
import CustomBackdrop from "../../../components/CustomBackdrop"
import ModalEmpleados from "../components/ModalEmpleados"

import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import es from "date-fns/locale/es"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
    secondary: {
      main: "#196C87",
    },
  },
})

const DIAS_SEMANA = {
  1: "Domingo",
  2: "Lunes",
  3: "Martes",
  4: "Miércoles",
  5: "Jueves",
  6: "Viernes",
  7: "Sábado",
}

// Componente de pestañas para selección de tipo de asignación
function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div role="tabpanel" hidden={value !== index} id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

const getAlertSeverity = (message) => {
  const lowerMessage = message.toLowerCase()

  if (lowerMessage.includes("error")) return "error"
  if (lowerMessage.includes("advertencia")) return "warning"
  if (lowerMessage.includes("success")) return "success"

  return "success" // valor por defecto
}

// Función para renderizar el contenido del evento personalizado
const renderEventContent = (eventInfo) => {
  const event = eventInfo.event
  const startTime = event.start
    ? new Date(event.start).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : ""

  const endTime = event.end
    ? new Date(event.end).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : ""

  return (
    <div
      style={{
        padding: "2px 4px",
        fontSize: "0.7rem",
        lineHeight: "1.1",
        overflow: "hidden",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
      }}
    >
      <div
        style={{
          fontWeight: 600,
          fontSize: "0.65rem",
          marginBottom: "1px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {startTime} - {endTime}
      </div>
      <div
        style={{
          wordWrap: "break-word",
          wordBreak: "break-word",
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2, // Máximo 2 líneas
          WebkitBoxOrient: "vertical",
          flex: 1,
        }}
      >
        {eventInfo.event.title}
      </div>
    </div>
  )
}

// ConflictAlert solo se usa en el tab de rango de fechas
const ConflictAlert = ({ message, conflicts, onClose }) => {
  const [showDetails, setShowDetails] = useState(false)

  return (
    <Alert severity="warning" onClose={onClose} sx={{ mb: 2 }}>
      <Typography variant="body1" gutterBottom>
        {message}
      </Typography>
      {conflicts.length > 0 && (
        <Box>
          <Button
            size="small"
            onClick={() => setShowDetails(!showDetails)}
            startIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ mt: 1 }}
          >
            {showDetails ? "Ocultar detalles" : `Ver detalles (${conflicts.length} fechas)`}
          </Button>
          {showDetails && (
            <Box
              sx={{
                mt: 1,
                p: 1,
                backgroundColor: "warning.light",
                borderRadius: 1,
                maxHeight: 150,
                overflow: "auto",
              }}
            >
              <Typography variant="body2" component="div">
                <strong>Fechas con conflictos:</strong>
                <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                  {conflicts.map((date, index) => (
                    <li key={index}>{date}</li>
                  ))}
                </ul>
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Alert>
  )
}

// Callback cuando se monta un evento
const handleEventDidMount = (info) => {
  const event = info.event
  const startTime = event.start
    ? new Date(event.start).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : ""

  const endTime = event.end
    ? new Date(event.end).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : ""

  const tooltip = `
    ${event.extendedProps.empleadoNombre}
    ${event.extendedProps.localidadNombre}
    ${startTime} - ${endTime}
    ${event.extendedProps.horarioDesc}
    ${event.extendedProps.plandescri || ""}
  `.trim()

  info.el.setAttribute("title", tooltip)
}

const CrearPlanificacionLabores = () => {
  const [eventos, setEventos] = useState([])
  const [conflictData, setConflictData] = useState({ message: "", conflicts: [] }) // Estado para detalles de conflictos solo para el tab de rango de fechas
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [conflictAlert, setConflictAlert] = useState("")
  const [loading, setLoading] = useState({
    empleados: true,
    localidades: true,
    horarios: true,
    guardando: false,
  })
  const [data, setData] = useState({
    empleados: [],
    localidades: [],
    horarios: [],
  })
  const [tabValue, setTabValue] = useState(0)
  const [rangoFechas, setRangoFechas] = useState({
    inicio: "",
    fin: "",
  })
  const [diasSeleccionados, setDiasSeleccionados] = useState({
    1: false, // Domingo
    2: false, // Lunes
    3: false, // Martes
    4: false, // Miércoles
    5: false, // Jueves
    6: false, // Viernes
    7: false, // Sábado
  })
  const [patronRepeticion, setPatronRepeticion] = useState("semanal")
  const [semanasRepeticion, setSemanasRepeticion] = useState(1)

  // Estados del formulario
  const [formData, setFormData] = useState({
    emcodemp: "",
    loccodigo: "",
    horcodigo: "",
    planfecha: "",
    plandescri: "",
  })

  // Modal para mostrar todos los empleados
  const [modalEmpleadosOpen, setModalEmpleadosOpen] = useState(false)

  // Cargar datos de las APIs
  useEffect(() => {
    fetchEmpleados()
    fetchLocalidades()
    fetchHorarios()
    fetchEventos() // Cargar eventos existentes al iniciar
  }, [])

  const fetchEmpleados = async () => {
    try {
      // Simulando llamada a API - reemplazar con la llamada real
      let response = await fetchwrapper(`/PlanificacionLabores/getAllEmpleados`)
      response = await response.json()

      // const response = await Promise.resolve({
      //   data: [
      //     {
      //       ciacodigo: "07",
      //       emcodemp: "000001",
      //       emnombre: "JESUS",
      //       agecodigo: "",
      //       horcodigo: "",
      //       hordescri: null,
      //       horini: null,
      //       horfin: null,
      //       hordiaini: null,
      //       hordiafin: null,
      //     },
      //     {
      //       ciacodigo: "07",
      //       emcodemp: "000002",
      //       emnombre: "MARIA",
      //       agecodigo: "",
      //       horcodigo: "",
      //       hordescri: null,
      //       horini: null,
      //       horfin: null,
      //       hordiaini: null,
      //       hordiafin: null,
      //     },
      //   ],
      // })

      setData((prev) => ({ ...prev, empleados: response.data }))
      setLoading((prev) => ({ ...prev, empleados: false }))
    } catch (error) {
      console.error("Error al cargar empleados:", error)
      setLoading((prev) => ({ ...prev, empleados: false }))
      Swal.fire({
        title: "Error",
        text: error?.details?.msg || "Error en la petición datos",
        icon: "error",
        confirmButtonText: "OK",
      })
      // throw new Error(errorResponse?.details?.msg || "Error en la petición datos")
    }
  }

  const fetchLocalidades = async () => {
    try {
      let response = await fetchwrapper(`/PlanificacionLabores/getAllLocalidades`)
      response = await response.json()
      // Simulando llamada a API - reemplazar con la llamada real
      // const response = await Promise.resolve({
      //   data: [
      //     {
      //       value: "01",
      //       label: "NAILS BOX CEIBOS [01]",
      //       loccodigo: "01",
      //       locdescri: "NAILS BOX CEIBOS",
      //     },
      //     {
      //       value: "02",
      //       label: "NAILS BOX CENTRO [02]",
      //       loccodigo: "02",
      //       locdescri: "NAILS BOX CENTRO",
      //     },
      //   ],
      // })

      setData((prev) => ({ ...prev, localidades: response.data }))
      setLoading((prev) => ({ ...prev, localidades: false }))
    } catch (error) {
      console.error("Error al cargar localidades:", error)
      setLoading((prev) => ({ ...prev, localidades: false }))
      Swal.fire({
        title: "Error",
        text: error?.details?.msg || "Error en la petición datos",
        icon: "error",
        confirmButtonText: "OK",
      })
    }
  }

  const fetchHorarios = async () => {
    try {
      let response = await fetchwrapper(`/PlanificacionLabores/getAllHorarios`)
      response = await response.json()
      // Simulando llamada a API - reemplazar con la llamada real
      // const response = await Promise.resolve({
      //   data: [
      //     {
      //       value: "01",
      //       label: "DE LUNES A DOMINGO 08:00:00 - 16:00:00",
      //       hordescri: "DE LUNES A DOMINGO",
      //       horini: "Mon, 27 Sep 1999 08:00:00 GMT",
      //       horfin: "Mon, 27 Sep 1999 16:00:00 GMT",
      //       hordiaini: 2,
      //       hordiafin: 6,
      //     },
      //     {
      //       value: "02",
      //       label: "FIN DE SEMANA 09:00:00 - 17:00:00",
      //       hordescri: "FIN DE SEMANA",
      //       horini: "Mon, 27 Sep 1999 09:00:00 GMT",
      //       horfin: "Mon, 27 Sep 1999 17:00:00 GMT",
      //       hordiaini: 1,
      //       hordiafin: 7,
      //     },
      //   ],
      // })

      setData((prev) => ({ ...prev, horarios: response.data }))
      setLoading((prev) => ({ ...prev, horarios: false }))
    } catch (error) {
      console.error("Error al cargar horarios:", error)
      setLoading((prev) => ({ ...prev, horarios: false }))
      Swal.fire({
        title: "Error",
        text: error?.details?.msg || "Error en la petición datos",
        icon: "error",
        confirmButtonText: "OK",
      })
    }
  }

  const fetchEventos = async () => {
    try {
      // Aquí iría la llamada real a la API para obtener eventos existentes
      // Por ahora simulamos una respuesta vacía
      let response = await fetchwrapper("/PlanificacionLabores/getAllEventsCalendar")
      response = await response.json()

      setEventos(response.data)
    } catch (error) {
      console.error("Error al cargar eventos:", error)
    }
  }

  const saveEventsCalendar = async (eventosData) => {
    try {
      setLoading((prev) => ({ ...prev, guardando: true }))

      // Llamada real a la API saveEventCalendar
      const response = await fetchwrapper("/PlanificacionLabores/saveEventsCalendar", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ events: eventosData }),
      })

      const result = await response.json()
      return result
    } catch (error) {
      console.error("Error al guardar eventos:", error)

      throw error
    } finally {
      setLoading((prev) => ({ ...prev, guardando: false }))
    }
  }

  // Nueva función para guardar por rango de fechas
  const saveEventsCalendarRangoFechas = async (eventData, rangeData) => {
    try {
      setLoading((prev) => ({ ...prev, guardando: true }))

      const payload = {
        eventData,
        rangeData,
      }

      const response = await fetchwrapper("/PlanificacionLabores/saveEventsCalendarRangoFechas", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      })

      const result = await response.json()

      // Si la respuesta tiene error, lanzar excepción
      if (result.error) {
        throw result.error
      }

      return result
    } catch (error) {
      console.error("Error al guardar eventos por rango:", error)
      throw error
    } finally {
      setLoading((prev) => ({ ...prev, guardando: false }))
    }
  }

  const verificarConflictos = (nuevoEvento) => {
    const empleadoEventos = eventos.filter((evento) => evento.extendedProps.emcodemp === nuevoEvento.emcodemp)

    for (const evento of empleadoEventos) {
      if (evento.start.split("T")[0] === nuevoEvento.planfecha) {
        return `El empleado ya tiene una tarea programada para esta fecha`
      }
    }
    return null
  }

  const handleDateSelect = (selectInfo) => {
    const fecha = selectInfo.start
    const fechaFormateada = formatDateToDDMMYYYY(fecha)

    setSelectedDate(fecha)
    setFormData({
      ...formData,
      planfecha: fechaFormateada,
    })
    setRangoFechas({
      inicio: fechaFormateada,
      fin: fechaFormateada,
    })
    setShowCreateModal(true)
  }

  const formatDateToDDMMYYYY = (date) => {
    const day = String(date.getDate()).padStart(2, "0")
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const year = date.getFullYear()
    return `${day}-${month}-${year}`
  }

  const parseDDMMYYYYToDate = (dateString) => {
    const [day, month, year] = dateString.split("-").map(Number)
    return new Date(year, month - 1, day)
  }

  const handleEventClick = (info) => {
    setSelectedEvent(info.event)
    setShowDetailsModal(true)
  }

  const generarFechasDesdeRango = () => {
    const fechas = []
    const inicio = parseDDMMYYYYToDate(rangoFechas.inicio)
    const fin = parseDDMMYYYYToDate(rangoFechas.fin)

    // Validar que las fechas sean válidas
    if (!inicio || !fin || isNaN(inicio) || isNaN(fin) || inicio > fin) {
      setConflictAlert("Error: Rango de fechas inválido")
      return []
    }

    // Calcular la diferencia en días
    const diffTiempo = fin.getTime() - inicio.getTime()
    const diffDias = Math.ceil(diffTiempo / (1000 * 60 * 60 * 24))

    // Usar for loop en lugar de while
    for (let i = 0; i <= diffDias; i++) {
      const fechaActual = new Date(inicio)
      fechaActual.setDate(inicio.getDate() + i)

      const diaSemana = fechaActual.getDay() + 1

      if (diasSeleccionados[diaSemana]) {
        fechas.push(formatDateToDDMMYYYY(fechaActual))
      }
    }

    return fechas
  }

  const generarFechasPorSemanas = (fechaInicio) => {
    const fechas = []
    const inicio = parseDDMMYYYYToDate(fechaInicio)

    // Validar que la fecha sea válida
    if (!inicio || isNaN(inicio)) {
      setConflictAlert("Error: Fecha inicial inválida")
      return []
    }

    for (let semana = 0; semana < semanasRepeticion; semana++) {
      for (let dia = 1; dia <= 7; dia++) {
        if (diasSeleccionados[dia]) {
          const fecha = new Date(inicio)
          // Corregir el cálculo de la fecha
          const diasASumar = semana * 7 + (dia - 1)
          fecha.setDate(inicio.getDate() + diasASumar)
          fechas.push(formatDateToDDMMYYYY(fecha))
        }
      }
    }

    return fechas
  }

  const crearTareasEnLote = async () => {
    setConflictAlert("")
    if (!formData.emcodemp || !formData.loccodigo || !formData.horcodigo || !formData.plandescri) {
      setConflictAlert("Advertencia: Por favor complete todos los campos obligatorios")
      return
    }

    const horarioSeleccionado = data.horarios.find((h) => h.value === formData.horcodigo)
    if (!horarioSeleccionado) {
      setConflictAlert("Advertencia: Horario no encontrado")
      return
    }

    // Preparar datos base del evento
    const eventoBase = {
      emcodemp: formData.emcodemp,
      loccodigo: formData.loccodigo,
      horcodigo: formData.horcodigo,
      horini: horarioSeleccionado.horini,
      horfin: horarioSeleccionado.horfin,
      hordiaini: horarioSeleccionado.hordiaini,
      hordiafin: horarioSeleccionado.hordiafin,
      plandescri: formData.plandescri || "Tarea programada",
    }

    try {
      let result

      if (tabValue === 0) {
        // Modo fecha única - usar API existente
        if (!formData.planfecha) {
          setConflictAlert("Advertencia: Por favor seleccione una fecha")
          return
        }

        const eventoUnico = {
          ...eventoBase,
          planfecha: formData.planfecha,
        }

        result = await saveEventsCalendar([eventoUnico])
      } else if (tabValue === 1) {
        // Modo rango de fechas - usar NUEVA API
        if (!rangoFechas.inicio || !rangoFechas.fin) {
          setConflictAlert("Advertencia: Por favor complete el rango de fechas")
          return
        }

        // Verificar que al menos un día esté seleccionado
        const hayDiasSeleccionados = Object.values(diasSeleccionados).some((selected) => selected)
        if (!hayDiasSeleccionados) {
          setConflictAlert("Advertencia: Debe seleccionar al menos un día de la semana")
          return
        }

        const rangeData = {
          startDate: rangoFechas.inicio,
          endDate: rangoFechas.fin,
          selectedDays: diasSeleccionados,
        }

        result = await saveEventsCalendarRangoFechas(eventoBase, rangeData)
      }

      // Recargar eventos desde el servidor
      await fetchEventos()

      // Mostrar mensaje de éxito
      const successMessage = result.msg

      // Mostrar SweetAlert de éxito
      await Swal.fire({
        title: "¡Éxito!",
        text: successMessage,
        icon: "success",
        confirmButtonText: "Aceptar",
        customClass: {
          container: "swal2-z-index",
        },
        didOpen: () => {
          const swalContainer = document.querySelector(".swal2-container")
          if (swalContainer) {
            swalContainer.style.zIndex = "99999"
          }
        },
      })

      setConflictData({ message: "", conflicts: [] })

      // Cerrar modal
      handleCloseCreateModal()
    } catch (error) {
      console.error("Error al guardar eventos:", error)

      // Manejar específicamente el error 409 (Conflictos) Aplica solo al tab de rango fechas
      if (error?.details?.status === 409) {
        const conflictDates = error?.details?.conflictDates || []
        // const conflictDetails = error?.details?.conflicts || []

        // Crear mensaje con HTML para mostrar los details
        setConflictData({
          message: `No se guardó ningún evento. Se encontraron conflictos en ${conflictDates.length} fechas.`,
          conflicts: conflictDates,
        })

        // Mostrar SweetAlert con detalles de conflictos
        await Swal.fire({
          title: "Conflictos Encontrados",
          html: `
          <div>
            <p>No se guardó ningún evento. Se encontraron conflictos en <strong>${conflictDates.length}</strong> fechas.</p>
            ${
              conflictDates.length > 0
                ? `
              <details style="margin-top: 10px;">
                <summary style="cursor: pointer; color: #196C87; font-weight: bold;">
                  Ver fechas con conflictos
                </summary>
                <div style="margin-top: 10px; max-height: 200px; overflow-y: auto;">
                  <ul style="text-align: left; padding-left: 20px;">
                    ${conflictDates.map((date) => `<li>${date}</li>`).join("")}
                  </ul>
                </div>
              </details>
            `
                : ""
            }
          </div>
        `,
          icon: "warning",
          confirmButtonText: "Entendido",
          customClass: {
            container: "swal2-z-index",
          },
          didOpen: () => {
            const swalContainer = document.querySelector(".swal2-container")
            if (swalContainer) {
              swalContainer.style.zIndex = "99999"
            }
          },
        })
      } else {
        // Manejar otros errores
        const errorMsg = error?.details?.msg || "Error al guardar las tareas. Intente nuevamente."
        setConflictAlert(`Error: ${errorMsg}`)

        await Swal.fire({
          title: "Error",
          text: errorMsg,
          icon: "error",
          confirmButtonText: "Aceptar",
          customClass: {
            container: "swal2-z-index",
          },
          didOpen: () => {
            const swalContainer = document.querySelector(".swal2-container")
            if (swalContainer) {
              swalContainer.style.zIndex = "99999"
            }
          },
        })
      }
    }
  }

  const deleteEventsCalendar = async (events) => {
    try {
      setLoading((prev) => ({ ...prev, guardando: true }))

      // Llamada real a la API deleteEventCalendar
      const response = await fetchwrapper("/PlanificacionLabores/deleteSpecificEventsCalendar", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ events }),
      })

      const result = await response.json()
      return result
    } catch (error) {
      console.error("Error al eliminar evento:", error)

      throw error
    } finally {
      setLoading((prev) => ({ ...prev, guardando: false }))
    }
  }

  const handleDeleteEvents = async (selectedEvents) => {
    if (selectedEvents.length > 0) {
      try {
        const result = await Swal.fire({
          title: "¿Estás seguro?",
          text: "¡No podrás revertir esta acción!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Sí, eliminar",
          cancelButtonText: "Cancelar",
          // Configuración de seguridad:
          allowOutsideClick: false,
          allowEscapeKey: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
          // Para que el usuario deba elegir explícitamente:
          focusConfirm: false,
          focusCancel: false,
          didOpen: () => {
            // Seleccionar el modal y aplicar z-index alto
            const modal = document.querySelector(".swal2-container")
            if (modal) {
              modal.style.zIndex = "999999"
            }
          },
        })
        if (!result.isConfirmed) return

        setLoading((prev) => ({ ...prev, guardando: true }))

        // Aquí iría la llamada a la API para eliminar el evento
        try {
          // Guardar eventos mediante la API
          await deleteEventsCalendar(selectedEvents)

          // Recargar eventos desde el servidor para reflejar los cambios
          await fetchEventos()

          // Cerrar modal después de un breve tiempo
          setShowDetailsModal(false)
          setSelectedEvent(null)
          // Mostrar mensaje de éxito
          await Swal.fire({
            title: "¡Eliminado!",
            text: `Se eliminaron ${selectedEvents.length} eventos.`,
            icon: "success",
            confirmButtonText: "Aceptar",
            didOpen: () => {
              // Seleccionar el modal y aplicar z-index alto
              const modal = document.querySelector(".swal2-container")
              if (modal) {
                modal.style.zIndex = "999999"
              }
            },
          })
        } catch (error) {
          console.error("Error al eliminar evento:", error)
          const msgError = error?.details?.msg || "Error al eliminar el evento. Intente nuevamente."
          await Swal.fire({
            title: "Error",
            text: msgError,
            icon: "error",
            confirmButtonText: "Aceptar",
            didOpen: () => {
              // Seleccionar el modal y aplicar z-index alto en caso de error también
              const modal = document.querySelector(".swal2-container")
              if (modal) {
                modal.style.zIndex = "999999"
              }
            },
          })
        }
      } catch (error) {
        console.error("Error al eliminar evento:", error)
      } finally {
        setLoading((prev) => ({ ...prev, guardando: false }))
      }
    }
  }

  const handleCloseCreateModal = () => {
    setShowCreateModal(false)
    setFormData({
      emcodemp: "",
      loccodigo: "",
      horcodigo: "",
      planfecha: "",
      plandescri: "",
    })
    setRangoFechas({
      inicio: "",
      fin: "",
    })
    setDiasSeleccionados({
      1: false,
      2: false,
      3: false,
      4: false,
      5: false,
      6: false,
      7: false,
    })
    setPatronRepeticion("semanal")
    setSemanasRepeticion(1)
    setConflictAlert("")
    setTabValue(0)
  }

  const handleDiaChange = (dia) => (event) => {
    setDiasSeleccionados({ ...diasSeleccionados, [dia]: event.target.checked })
  }

  const getEventosPorEmpleado = () => {
    const eventosPorEmpleado = {}
    eventos.forEach((evento) => {
      const emcodemp = evento.extendedProps.emcodemp
      if (!eventosPorEmpleado[emcodemp]) {
        eventosPorEmpleado[emcodemp] = []
      }
      eventosPorEmpleado[emcodemp].push(evento)
    })
    return eventosPorEmpleado
  }

  const seleccionarTodosDias = (seleccionar) => {
    const nuevosDias = {}
    for (let i = 1; i <= 7; i++) {
      nuevosDias[i] = seleccionar
    }
    setDiasSeleccionados(nuevosDias)
  }

  const isLoading = loading.empleados || loading.localidades || loading.horarios

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <BackIcon />
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: "0 30px 30px 30px",
              fontSize: "25px",
            }}
          >
            <b>Planificación de Tareas</b>
          </div>

          {isLoading ? (
            <CustomBackdrop isLoading={isLoading} />
          ) : (
            <Box className="row">
              {/* Calendar Sidebar */}
              <div className="col-xxl-3 col-xl-4 theiaStickySidebar">
                <div className="stickybar">
                  <div className="card">
                    <div className="card-body p-3">
                      {/* Botón para abrir modal de empleados */}
                      <div className="border-bottom pb-4 mb-4">
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <h5 className="mb-0">Empleados</h5>
                          <IconButton
                            size="small"
                            onClick={() => setModalEmpleadosOpen(true)}
                            sx={{
                              backgroundColor: "primary.main",
                              color: "white",
                              "&:hover": { backgroundColor: "primary.dark" },
                            }}
                          >
                            <Groups />
                          </IconButton>
                        </Box>
                        <Typography variant="body2" color="textSecondary">
                          Total: {data.empleados.length} empleados
                        </Typography>
                        <Button fullWidth variant="outlined" onClick={() => setModalEmpleadosOpen(true)} sx={{ mt: 1 }}>
                          Ver todos los empleados
                        </Button>
                      </div>

                      {/* Estadísticas */}
                      <div className="border-bottom pb-4 mb-4">
                        <h5 className="mb-2">
                          Tareas Totales
                          <span className="badge badge-success rounded-pill ms-2">{eventos.length}</span>
                        </h5>
                        <Box display="flex" gap={1} mt={1}>
                          <Chip label={`${eventos.length}`} size="small" color="primary" variant="outlined" />
                        </Box>
                      </div>

                      {/* Localidades */}
                      <div className="mb-4">
                        <h6 className="mb-2">Localidades Disponibles</h6>
                        {data.localidades.map((localidad) => (
                          <div key={localidad.loccodigo} className="d-flex align-items-center mb-1">
                            <i className="ti ti-map-pin text-info me-2"></i>
                            <small>
                              {localidad.locdescri} [{localidad.loccodigo}]
                            </small>
                          </div>
                        ))}
                      </div>

                      {/* Horarios */}
                      <div className="mb-4">
                        <h6 className="mb-2">Horarios Disponibles</h6>
                        {data.horarios.map((horario) => (
                          <div key={horario.value} className="d-flex align-items-center mb-1">
                            <i className="ti ti-clock text-info me-2"></i>
                            <small>
                              {horario.hordescri} ({DIAS_SEMANA[horario.hordiaini]} a {DIAS_SEMANA[horario.hordiafin]})
                            </small>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Calendar Main */}
              <div className="col-xxl-9 col-xl-8 theiaStickySidebar">
                <div className="stickybar">
                  <div className="card border-0">
                    <div className="card-body" style={{ padding: "10px" }}>
                      <FullCalendar
                        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                        initialView="dayGridMonth"
                        locale={esLocale}
                        firstDay={0}
                        events={eventos}
                        headerToolbar={{
                          start: "today,prev,next",
                          center: "title",
                          end: "dayGridMonth,timeGridWeek,timeGridDay",
                        }}
                        eventClick={handleEventClick}
                        select={handleDateSelect}
                        selectable={true}
                        height="auto"
                        eventDisplay="block"
                        dayMaxEvents={true} // Cambia a true para mostrar "más" link
                        dayMaxEventRows={true} // Muestra todos los eventos con scroll
                        eventMaxStack={3} // Máximo de eventos apilados
                        eventOrder="start,title" // Ordenar eventos por hora y título
                        eventTimeFormat={{
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        }}
                        eventContent={renderEventContent} // Función personalizada para renderizar eventos
                        views={{
                          dayGridMonth: {
                            dayMaxEventRows: 4, // Límite de filas por día
                            dayMaxEvents: 10, // Límite de eventos por día
                          },
                          timeGridWeek: {
                            dayMaxEvents: 20, // Para vista semanal
                          },
                          timeGridDay: {
                            dayMaxEvents: 30, // Para vista diaria
                          },
                        }}
                        eventDidMount={handleEventDidMount} // Callback cuando se monta un evento
                        // NUEVAS PROPIEDADES PARA MÓVILES
                        selectMirror={true}
                        selectLongPressDelay={100}
                        eventLongPressDelay={100}
                        longPressDelay={100}
                      />
                    </div>
                  </div>
                </div>
                {conflictData.message && (
                  <ConflictAlert
                    message={conflictData.message}
                    conflicts={conflictData.conflicts}
                    onClose={() => setConflictData({ message: "", conflicts: [] })}
                  />
                )}
              </div>
            </Box>
          )}
          {/* Modal de Empleados */}
          <ModalEmpleados
            open={modalEmpleadosOpen}
            onClose={() => setModalEmpleadosOpen(false)}
            empleados={data.empleados}
            eventos={eventos}
            onDeleteEvents={handleDeleteEvents}
            loading={isLoading}
          />

          {/* Modal Crear Evento */}
          <Dialog open={showCreateModal} onClose={handleCloseCreateModal} maxWidth="md" fullWidth>
            <DialogTitle>Programar Tareas</DialogTitle>
            <DialogContent>
              {conflictAlert && (
                <Alert severity={getAlertSeverity(conflictAlert)} sx={{ mb: 2 }}>
                  {conflictAlert}
                </Alert>
              )}

              {conflictData.message && (
                <ConflictAlert
                  message={conflictData.message}
                  conflicts={conflictData.conflicts}
                  onClose={() => setConflictData({ message: "", conflicts: [] })}
                />
              )}

              {loading.guardando && (
                <Box display="flex" justifyContent="center" alignItems="center" my={2}>
                  <CircularProgress size={24} sx={{ mr: 2 }} />
                  <Typography>Guardando tareas...</Typography>
                </Box>
              )}

              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                  <Tab label="Fecha única" />
                  <Tab label="Por rango de fechas" />
                  {/* <Tab label="Por semanas" /> */}
                </Tabs>
              </Box>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Empleado</InputLabel>
                    <Select
                      value={formData.emcodemp}
                      onChange={(e) => setFormData({ ...formData, emcodemp: e.target.value })}
                      label="Empleado"
                    >
                      {data.empleados.map((empleado) => (
                        <MenuItem key={empleado.emcodemp} value={empleado.emcodemp}>
                          {empleado.emnombre} ({empleado.emcodemp})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Localidad</InputLabel>
                    <Select
                      value={formData.loccodigo}
                      onChange={(e) => setFormData({ ...formData, loccodigo: e.target.value })}
                      label="Localidad"
                    >
                      {data.localidades.map((localidad) => (
                        <MenuItem key={localidad.loccodigo} value={localidad.loccodigo}>
                          {localidad.locdescri} [{localidad.loccodigo}]
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Horario</InputLabel>
                    <Select
                      value={formData.horcodigo}
                      onChange={(e) => setFormData({ ...formData, horcodigo: e.target.value })}
                      label="Horario"
                    >
                      {data.horarios.map((horario) => (
                        <MenuItem key={horario.value} value={horario.value}>
                          {horario.hordescri} ({DIAS_SEMANA[horario.hordiaini]} a {DIAS_SEMANA[horario.hordiafin]})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descripción de la tarea"
                    multiline
                    rows={3}
                    value={formData.plandescri}
                    onChange={(e) => setFormData({ ...formData, plandescri: e.target.value })}
                  />
                </Grid>
                {/* Panel para fecha única */}
                <TabPanel value={tabValue} index={0} sx={{ p: 0, width: "100%" }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <DatePicker
                        label="Seleccionar fecha"
                        value={formData.planfecha ? parseDDMMYYYYToDate(formData.planfecha) : null}
                        onChange={(newDate) => {
                          if (newDate) {
                            setFormData({
                              ...formData,
                              planfecha: formatDateToDDMMYYYY(newDate),
                            })
                          }
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            placeholder: "DD-MM-YYYY",
                          },
                        }}
                        format="dd-MM-yyyy"
                      />
                    </Grid>
                  </Grid>
                </TabPanel>
                {/* Panel para rango de fechas */}
                <TabPanel value={tabValue} index={1}>
                  <Grid container spacing={3}>
                    {/* Fechas en una sola fila con mejor espaciado */}
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                        <DatePicker
                          label="Fecha inicio"
                          value={rangoFechas.inicio ? parseDDMMYYYYToDate(rangoFechas.inicio) : null}
                          onChange={(newDate) => {
                            if (newDate) {
                              setRangoFechas({ ...rangoFechas, inicio: formatDateToDDMMYYYY(newDate) })
                            }
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              placeholder: "DD-MM-YYYY",
                              sx: {
                                "& .MuiInputLabel-root": {
                                  color: "text.primary",
                                  fontWeight: 500,
                                },
                              },
                            },
                          }}
                          format="dd-MM-yyyy"
                        />
                        <DatePicker
                          label="Fecha fin"
                          value={rangoFechas.fin ? parseDDMMYYYYToDate(rangoFechas.fin) : null}
                          onChange={(newDate) => {
                            if (newDate) {
                              setRangoFechas({ ...rangoFechas, fin: formatDateToDDMMYYYY(newDate) })
                            }
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              placeholder: "DD-MM-YYYY",
                              sx: {
                                "& .MuiInputLabel-root": {
                                  color: "text.primary",
                                  fontWeight: 500,
                                },
                              },
                            },
                          }}
                          format="dd-MM-yyyy"
                        />
                      </Box>
                    </Grid>

                    {/* Sección de días de la semana con mejor diseño */}
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                          p: 2,
                          backgroundColor: "background.default",
                        }}
                      >
                        {/* Header con botones y título */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              color: "text.primary",
                            }}
                          >
                            📅 Días de la semana
                          </Typography>

                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => seleccionarTodosDias(true)}
                              // startIcon={<CheckBoxIcon />}
                              sx={{
                                fontSize: "0.75rem",
                                textTransform: "none",
                              }}
                            >
                              Todos
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => seleccionarTodosDias(false)}
                              // startIcon={<CheckBoxOutlineBlankIcon />}
                              sx={{
                                fontSize: "0.75rem",
                                textTransform: "none",
                              }}
                            >
                              Ninguno
                            </Button>
                          </Box>
                        </Box>

                        {/* Grid de días de la semana */}
                        <FormGroup>
                          <Grid container spacing={1}>
                            {Object.keys(DIAS_SEMANA).map((dia) => (
                              <Grid item xs={4} sm={3} md={2.4} key={dia}>
                                <Paper
                                  elevation={diasSeleccionados[dia] ? 1 : 0}
                                  sx={{
                                    p: 1,
                                    border: "1px solid",
                                    borderColor: diasSeleccionados[dia] ? "primary.main" : "divider",
                                    backgroundColor: diasSeleccionados[dia] ? "primary.light" : "transparent",
                                    borderRadius: 1,
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                      backgroundColor: diasSeleccionados[dia] ? "primary.light" : "action.hover",
                                    },
                                  }}
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={diasSeleccionados[dia]}
                                        onChange={handleDiaChange(dia)}
                                        size="small"
                                        sx={{
                                          mr: 0.5,
                                          color: diasSeleccionados[dia] ? "primary.main" : "text.secondary",
                                        }}
                                      />
                                    }
                                    label={
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: diasSeleccionados[dia] ? 600 : 400,
                                          color: diasSeleccionados[dia] ? "primary.main" : "text.primary",
                                        }}
                                      >
                                        {DIAS_SEMANA[dia].substring(0, 3)}
                                      </Typography>
                                    }
                                    sx={{
                                      m: 0,
                                      width: "100%",
                                      "& .MuiFormControlLabel-label": { flex: 1 },
                                    }}
                                  />
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        </FormGroup>

                        {/* Contador de días seleccionados */}
                        <Box
                          sx={{
                            mt: 2,
                            pt: 1,
                            borderTop: "1px solid",
                            borderColor: "divider",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Días seleccionados:
                          </Typography>
                          <Chip
                            label={Object.values(diasSeleccionados).filter(Boolean).length}
                            size="small"
                            color="primary"
                            variant="filled"
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </TabPanel>
                {/* Panel para semanas */}
                <TabPanel value={tabValue} index={2}>
                  <Grid item xs={12} sm={6}>
                    NO VALEEEEEEE
                    <TextField
                      fullWidth
                      label="Fecha inicial (DD-MM-YYYY)"
                      value={formData.planfecha}
                      onChange={(e) => setFormData({ ...formData, planfecha: e.target.value })}
                      placeholder="DD-MM-YYYY"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Número de semanas"
                      value={semanasRepeticion}
                      onChange={(e) => setSemanasRepeticion(parseInt(e.target.value) || 1)}
                      inputProps={{ min: 1, max: 12 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">Patrón de repetición</FormLabel>
                      <RadioGroup value={patronRepeticion} onChange={(e) => setPatronRepeticion(e.target.value)}>
                        <FormControlLabel value="semanal" control={<Radio />} label="Semanal" />
                        <FormControlLabel value="quincenal" control={<Radio />} label="Quincenal" />
                        <FormControlLabel value="mensual" control={<Radio />} label="Mensual" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Días de la semana:
                    </Typography>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Button size="small" onClick={() => seleccionarTodosDias(true)}>
                        Seleccionar todos
                      </Button>
                      <Button size="small" onClick={() => seleccionarTodosDias(false)}>
                        Deseleccionar todos
                      </Button>
                    </Box>
                    <FormGroup row>
                      {Object.keys(DIAS_SEMANA).map((dia) => (
                        <FormControlLabel
                          key={dia}
                          control={<Checkbox checked={diasSeleccionados[dia]} onChange={handleDiaChange(dia)} />}
                          label={DIAS_SEMANA[dia].substring(0, 3)}
                        />
                      ))}
                    </FormGroup>
                  </Grid>
                </TabPanel>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseCreateModal} disabled={loading.guardando}>
                Cancelar
              </Button>
              <Button onClick={crearTareasEnLote} variant="contained" disabled={loading.guardando}>
                {tabValue === 0 ? "Programar Tarea" : "Programar Tareas en Lote"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Modal Detalles del Evento */}
          <Dialog open={showDetailsModal} onClose={() => setShowDetailsModal(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Detalles de la Tarea</DialogTitle>
            <DialogContent>
              {selectedEvent && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <Typography variant="h6">{selectedEvent.extendedProps.empleadoNombre}</Typography>
                    <Chip
                      label={selectedEvent.extendedProps.localidadNombre}
                      color="primary"
                      size="small"
                      sx={{ mb: 2 }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Fecha:
                    </Typography>
                    <Typography>{new Date(selectedEvent.start).toLocaleDateString()}</Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Horario:
                    </Typography>
                    <Typography>{selectedEvent.extendedProps.horarioDesc}</Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Hora de Inicio:
                    </Typography>
                    <Typography>
                      {new Date(selectedEvent.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Hora de Fin:
                    </Typography>
                    <Typography>
                      {new Date(selectedEvent.end).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Descripción:
                    </Typography>
                    <Typography>{selectedEvent.extendedProps.plandescri || "Sin descripción"}</Typography>
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => handleDeleteEvents([selectedEvent.extendedProps])} color="error">
                Eliminar
              </Button>
              <Button onClick={() => setShowDetailsModal(false)}>Cerrar</Button>
            </DialogActions>
          </Dialog>
        </div>
      </LocalizationProvider>
    </ThemeProvider>
  )
}

export default CrearPlanificacionLabores
